/**
 * Eine Karte des Audit-Zeitstrahls (REQ-26 §2.2): wer wann was geändert hat. Vorlage ist
 * `AuditEntry.tsx` der ChinaApp.
 *
 * Die alte Tabelle zeigte je Satz die rohen JSON-Zeichenketten. Hier steht je Feld eine Zeile
 * `Beschriftung: alt → neu`; das JSON bleibt für den Support erreichbar, aber nur auf Wunsch.
 */

import { Chip, Icon } from '@/components/ui';

import {
  EMPTY_VALUE,
  type AuditChange,
  type AuditKind,
  type AuditRawText,
  type AuditRecord,
} from './auditDiff';

/** Vorhandene Chip-Varianten; Unbekanntes bleibt neutral. */
const CHIP_BY_KIND: Readonly<Record<AuditKind, string | undefined>> = {
  created: 'e-success',
  updated: 'e-info',
  deleted: 'e-danger',
  unknown: undefined,
};

const ICON_BY_KIND: Readonly<Record<AuditKind, string>> = {
  created: 'fa-solid fa-file-circle-plus',
  updated: 'fa-solid fa-pen',
  deleted: 'fa-solid fa-trash',
  unknown: 'fa-solid fa-triangle-exclamation',
};

function ChangeRow({ change }: { change: AuditChange }) {
  // Beim Anlegen gibt es kein Vorher, beim Löschen kein Nachher — ein Pfeil auf eine leere
  // Seite wäre bei 60 Feldern einer Neuanlage nur Rauschen.
  const showOld = change.hasOld;
  const showNew = change.hasNew;

  return (
    <div className="iwm-audit-change">
      <dt className="iwm-audit-label" title={change.field}>
        {change.label}
      </dt>
      <dd className="iwm-audit-values">
        {showOld ? (
          <span
            className="iwm-audit-old"
            data-empty={change.oldText === EMPTY_VALUE ? '' : undefined}
          >
            {change.oldText}
          </span>
        ) : null}
        {showOld && showNew ? (
          <>
            <Icon iconClass="fa-solid fa-arrow-right" className="iwm-audit-arrow" />
            <span className="iwm-audit-sr-only">geändert zu</span>
          </>
        ) : null}
        {showNew ? (
          <span
            className="iwm-audit-new"
            data-empty={change.newText === EMPTY_VALUE ? '' : undefined}
          >
            {change.newText}
          </span>
        ) : null}
      </dd>
    </div>
  );
}

const RAW_ROWS: readonly (readonly [string, keyof AuditRawText])[] = [
  ['AffectedColumns', 'affectedColumns'],
  ['OldValues', 'oldValues'],
  ['NewValues', 'newValues'],
];

function RawBlock({ raw }: { raw: AuditRawText }) {
  return (
    <div className="iwm-audit-raw">
      {RAW_ROWS.map(([label, key]) => (
        <div className="iwm-audit-raw-row" key={key}>
          <span className="iwm-audit-raw-label">{label}</span>
          <pre className="iwm-audit-raw-text">{raw[key] ?? EMPTY_VALUE}</pre>
        </div>
      ))}
    </div>
  );
}

export interface AuditEntryProps {
  record: AuditRecord;
  /** Blendet die ursprünglichen JSON-Zeichenketten ein — für den Support. */
  showRaw?: boolean;
}

export function AuditEntry({ record, showRaw = false }: AuditEntryProps) {
  const { unreadable } = record;

  return (
    <li className="iwm-audit-item">
      <span className="iwm-audit-marker" data-kind={record.kind} aria-hidden="true">
        <Icon iconClass={ICON_BY_KIND[record.kind]} />
      </span>

      <article className="iwm-audit-card">
        <header className="iwm-audit-head">
          <time className="iwm-audit-time" dateTime={record.timestampIso}>
            {record.timestampText}
          </time>
          <span className="iwm-audit-user" title={record.username}>
            {record.username}
          </span>
          <Chip
            text={record.kindLabel}
            cssClass={CHIP_BY_KIND[record.kind]}
            className="iwm-audit-kind"
          />
        </header>

        {unreadable === null ? null : (
          <div className="iwm-audit-unreadable">
            <p className="iwm-audit-warning">
              <Icon iconClass="fa-solid fa-triangle-exclamation" />
              Die gespeicherten Werte sind kein gültiges JSON. Hier steht der Rohtext.
            </p>
            {unreadable.oldValues === null ? null : (
              <pre className="iwm-audit-raw-text">{unreadable.oldValues}</pre>
            )}
            {unreadable.newValues === null ? null : (
              <pre className="iwm-audit-raw-text">{unreadable.newValues}</pre>
            )}
          </div>
        )}

        {record.changes.length > 0 ? (
          <dl className="iwm-audit-changes">
            {record.changes.map((change) => (
              <ChangeRow change={change} key={change.field} />
            ))}
          </dl>
        ) : null}

        {record.changes.length === 0 && unreadable === null ? (
          <p className="iwm-audit-note">Zu diesem Eintrag sind keine Werte verzeichnet.</p>
        ) : null}

        {showRaw ? <RawBlock raw={record.raw} /> : null}
      </article>
    </li>
  );
}

export default AuditEntry;
